/**
 * Política de pagamento (client) — espelho de functions/src/utils/paymentPolicy.ts.
 * Usar para UI de confirmação PIX / gates de despacho.
 */

export type PaymentTiming = 'PREPAID' | 'ON_DELIVERY';

export const ALLOW_ON_DELIVERY_PAYMENT = false;

function stripAccents(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function normalizePaymentMethod(method?: string): string {
  const raw = stripAccents(String(method || ''))
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!raw) return '';

  if (raw === 'PIX' || raw.startsWith('PIX_') || raw.includes('_PIX_') || raw.endsWith('_PIX')) {
    return 'PIX';
  }

  if (
    raw === 'CASH' ||
    raw === 'DINHEIRO' ||
    raw === 'MONEY' ||
    raw === 'CASH_ON_DELIVERY' ||
    raw === 'DINHEIRO_NA_ENTREGA' ||
    raw.includes('DINHEIRO')
  ) {
    return 'CASH';
  }

  if (
    raw === 'CREDIT' ||
    raw === 'CREDIT_CARD' ||
    raw === 'CARTAO_CREDITO' ||
    raw === 'CARTAO_DE_CREDITO' ||
    raw === 'CREDITO' ||
    raw === 'CARTAO_CREDITO_NA_ENTREGA' ||
    raw.includes('CREDITO') ||
    (raw.includes('CREDIT') && !raw.includes('DEBIT'))
  ) {
    return 'CREDIT';
  }

  if (
    raw === 'DEBIT' ||
    raw === 'DEBIT_CARD' ||
    raw === 'CARTAO_DEBITO' ||
    raw === 'CARTAO_DE_DEBITO' ||
    raw === 'DEBITO' ||
    raw === 'CARTAO_DEBITO_NA_ENTREGA' ||
    raw.includes('DEBITO') ||
    raw.includes('DEBIT')
  ) {
    return 'DEBIT';
  }

  if (raw === 'CARTAO' || (raw.includes('CARTAO') && !raw.includes('PIX'))) {
    return 'CREDIT';
  }

  return raw;
}

export function isPrepaid(method?: string): boolean {
  return normalizePaymentMethod(method) === 'PIX';
}

export function isOnDelivery(method?: string): boolean {
  const m = normalizePaymentMethod(method);
  return m === 'CASH' || m === 'CREDIT' || m === 'DEBIT';
}

export function getPaymentTiming(method?: string): PaymentTiming {
  return isOnDelivery(method) ? 'ON_DELIVERY' : 'PREPAID';
}

export const resolvePaymentTiming = getPaymentTiming;

export function isPaymentConfirmed(status?: string | null | unknown): boolean {
  return status === 'paid' || status === 'confirmed';
}

export function requiresPaymentBeforeDispatch(method?: string): boolean {
  return isPrepaid(method);
}

export function canUseOnDeliveryPayment(opts: { deliveryType?: string }): boolean {
  return ALLOW_ON_DELIVERY_PAYMENT && opts.deliveryType === 'OWN_DRIVER';
}

/** Microcópia OS 07-b5 — botão desabilitado (nunca oculto). */
export const DISPATCH_NEEDS_PIX_MESSAGE =
  'Confirme o PIX para liberar a entrega';

export function assertCanDispatchOrder(order: {
  paymentMethod?: string;
  paymentStatus?: string | null;
}): { ok: true } | { ok: false; message: string } {
  if (
    requiresPaymentBeforeDispatch(order.paymentMethod) &&
    !isPaymentConfirmed(order.paymentStatus)
  ) {
    return {
      ok: false,
      message: DISPATCH_NEEDS_PIX_MESSAGE,
    };
  }
  return { ok: true };
}
